<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：お知らせ画面
 *
 * 作成者：2023/06/19 ノア）馬
 * 最終修正者：2023/06/19 ノア）馬
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button
            class="backButton"
            @click="onBackButtonClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.back')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('gpsTracker.announcementsInfo.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <div class="tti-content-title">
        <el-form
          class="tti-form announcement-form-style"
          @submit.native.prevent
          ref="form"
          :model="announcementForm"
          :rules="formRules"
        >
          <el-form-item :label="$t('gpsTracker.announcementsInfo.title')" prop="title" label-width="120px">
            <el-input
              size="small"
              v-model.trim="announcementForm.title"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbTitle')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            ></el-input>
          </el-form-item>
          <el-form-item :label="$t('gpsTracker.announcementsInfo.context')" prop="context" label-width="120px">
            <el-input
              type="textarea"
              resize="none"
              :rows="6"
              v-model.trim="announcementForm.context"
              v-tooltip="{
                content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbContext')}`,
                placement: 'bottom-start',
                delay: { show: 1000, hide: 0 }
              }"
            ></el-input>
          </el-form-item>
        </el-form>
        <div class="tti-form-item-right">
          <el-form class="tti-form announcement-form-style">
            <div class="public-target">
              <!-- 公開対象 -->
              <span class="required-star-group">*</span>
              <el-form-item :label="$t('gpsTracker.announcementsInfo.publicTarget')" prop="radio" label-width="80px">
                <el-radio-group
                  v-model="announcementForm.publicTargetType"
                  @change="clearCompany"
                  class="radio-group"
                  class-name="required"
                >
                  <!-- ログイン画面 -->
                  <el-radio :label="publicTargetCode.LOGIN">{{
                    $t('gpsTracker.announcementsInfo.publicLogin')
                  }}</el-radio>
                  <!-- 全会社 -->
                  <div>
                    <el-radio :label="publicTargetCode.ALL" class="radio-company">{{
                      $t('gpsTracker.announcementsInfo.publicAllCompany')
                    }}</el-radio>
                  </div>

                  <!-- 会社 -->
                  <div class="select-company">
                    <el-radio :label="publicTargetCode.COMPANY" class="radio-company">{{
                      $t('gpsTracker.announcementsInfo.publicCompany')
                    }}</el-radio>
                    <el-form-item
                      class="select-company-item"
                      v-if="announcementForm.publicTargetType === publicTargetCode.COMPANY"
                    >
                      <el-select
                        size="small"
                        v-model="announcementForm.publicTarget"
                        multiple
                        collapse-tags
                        v-tooltip="{
                          content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbSelect', [
                            $t('gpsTracker.announcementsInfo.publicCompany')
                          ])}`,
                          placement: 'bottom-start',
                          delay: { show: 1000, hide: 0 }
                        }"
                      >
                        <el-option
                          v-for="item in companyList"
                          :key="item.code"
                          :label="item.name"
                          :value="item.code"
                          class="custom-option"
                        >
                        </el-option>
                      </el-select>
                    </el-form-item>
                  </div>
                </el-radio-group>
              </el-form-item>
            </div>

            <!-- 公開 -->
            <el-form-item :label="$t('gpsTracker.announcementsInfo.publicFlag')" label-width="86px">
              <el-checkbox
                size="small"
                v-model="announcementForm.publicFlag"
                v-tooltip="{
                  content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbSelect', [
                    $t('gpsTracker.announcementsInfo.publicFlag')
                  ])}`,
                  placement: 'bottom-start',
                  delay: { show: 1000, hide: 0 }
                }"
              >
              </el-checkbox>
            </el-form-item>

            <div class="public-period">
              <!-- 公開期間 -->
              <span class="required-star-date">*</span>
              <el-form-item :label="$t('gpsTracker.announcementsInfo.publicPeriod')" label-width="80px">
                <el-date-picker
                  v-model="announcementForm.publicStartDate"
                  type="datetime"
                  format="yyyy/MM/dd HH:mm"
                  value-format="yyyy/MM/dd HH:mm"
                  :editable="false"
                  :clearable="false"
                  :picker-options="pickerOptions"
                  size="small"
                  v-tooltip="{
                    content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbSelect', [
                      $t('gpsTracker.announcementsInfo.publicStartDate')
                    ])}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>

                <span class="conn">{{ '~' }}</span>
                <el-date-picker
                  v-model="announcementForm.publicEndDate"
                  type="datetime"
                  format="yyyy/MM/dd HH:mm"
                  value-format="yyyy/MM/dd HH:mm"
                  :editable="false"
                  :clearable="false"
                  :picker-options="pickerOptions"
                  size="small"
                  v-tooltip="{
                    content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.tbSelect', [
                      $t('gpsTracker.announcementsInfo.publicEndDate')
                    ])}`,
                    placement: 'bottom-start',
                    delay: { show: 1000, hide: 0 }
                  }"
                ></el-date-picker>
              </el-form-item>
            </div>
          </el-form>
        </div>
      </div>

      <div class="tti-middle-area">
        <div class="tti-bottom-left-area"></div>
        <div class="tti-bottom-right-area">
          <!-- 取消-->
          <el-button
            class="tti-btn-default tti-btn-001"
            @click="onCancelClick"
            v-tooltip="getTooltip(tooltipType.cancel)"
          >
            <tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}
          </el-button>
          <!-- 修正登録 -->
          <el-button
            class="tti-btn-default tti-btn-005"
            @click="onEditClick"
            :disabled="!singleSelection"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.btnCorrectionRegist')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('buttons.correctionRegist') }}
          </el-button>
          <!-- 新規登録 -->
          <el-button
            class="tti-btn-default tti-btn-005"
            @click="onAddClick"
            v-tooltip="{
              content: `${$t('gpsTracker.tooltipCommon.announcementsInfo.btnNewRegist')}`,
              placement: 'bottom-start',
              delay: { show: 1000, hide: 0 }
            }"
          >
            {{ $t('buttons.newRegist') }}
          </el-button>
        </div>
      </div>
      <el-table
        ref="tableRef"
        :data="tableData"
        height="calc(96vh - 400px)"
        :border="true"
        :stripe="true"
        header-row-class-name="tti-master-table-header"
        :empty-text="$t('messages.common.emptyTableData')"
        highlight-current-row
        @current-change="handleCurrentChange"
      >
        <!-- 件名 -->
        <el-table-column prop="title" :label="$t('gpsTracker.announcementsInfo.title')" width="320"></el-table-column>
        <!-- 内容 -->
        <el-table-column
          prop="context"
          :label="$t('gpsTracker.announcementsInfo.context')"
          width="900"
        ></el-table-column>
        <!-- 公開対象 -->
        <el-table-column
          prop="publicTarget"
          :label="$t('gpsTracker.announcementsInfo.publicTarget')"
          width="200"
          :formatter="publicTargetFormat"
        ></el-table-column>
        <!-- 公開 -->
        <el-table-column
          prop="publicFlag"
          :label="$t('gpsTracker.announcementsInfo.publicFlag')"
          width="80"
          align="center"
          :formatter="publicFlagFormat"
        ></el-table-column>
        <!-- 公開期間 -->
        <el-table-column
          prop="publicPeriod"
          :label="$t('gpsTracker.announcementsInfo.publicPeriod')"
          width="157"
        ></el-table-column>
        <!-- 最終修正時刻 -->
        <el-table-column
          prop="sendDate"
          :label="$t('gpsTracker.announcementsInfo.sendDate')"
          width="200"
          align="center"
        ></el-table-column>
      </el-table>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref, Vue } from 'vue-property-decorator'
import { Table, Form } from 'element-ui'
import { cloneDeep } from 'lodash'

import TOOL_COMPANY_BASE from '@/views/companyEditTool/TOOL_COMPANY_BASE.vue'
import { i18n } from '@/locales'
import { RULES, validateService } from '@/service/validateService'
import {
  General,
  ToolAnnouncementData,
  ToolAnnouncementFormData,
  ToolAnnouncementUpdateParam,
  ToolCompanyMasterData
} from '@/models'
import { isDateAfter, pickerOptionsPast } from '@/utils/dateUtils'
import { addAnnounce, getAnnouncementListData, getToolCompanyInfoData, updateAnnounce } from '@/store/apis'
import helperService from '@/service/helperService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import { ToolAnnouncementPublicFlag, ToolAnnouncementPublicTarget } from '@/commons/constants'

@Component({
  name: 'TOOL_ANNOUNCEMENTS_INFO'
})
export default class TOOL_ANNOUNCEMENTS_INFO extends TOOL_COMPANY_BASE {
  @Ref('tableRef') readonly tableRef!: Table

  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptionsPast

  /**
   * 画面ID
   */
  pageId = 'TOOL_ANNOUNCEMENTS_INFO'

  /**
   * 古いデータ
   */
  oldData: ToolAnnouncementFormData = {} as ToolAnnouncementFormData

  announcementForm: ToolAnnouncementFormData = {
    title: '',
    context: '',
    publicTarget: [],
    publicFlag: false,
    publicStartDate: '',
    publicEndDate: '',
    publicPeriod: '',
    publicTargetType: ToolAnnouncementPublicTarget.LOGIN
  } as ToolAnnouncementFormData

  /**
   * 変更したデータ
   */
  tableData: Array<ToolAnnouncementData> = []

  /**
   * テーブル行で選択したデータ
   */
  singleSelection: ToolAnnouncementData | null = null

  /**
   * 会社情報ID
   */
  companyId = ''

  /**
   * 会社リスト
   */
  companyList: General[] = []

  /**
   * 公開対象ステータス
   */
  publicTargetCode = ToolAnnouncementPublicTarget

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  async mounted(): Promise<void> {
    this.$log.debug('TOOL_ANNOUNCEMENTS_INFO > mounted')
    this.initConfig()
    this.fetchTableData()
    this.fetchCompanyData()
  }
  /**
   * 関連データの取得
   */
  initConfig(): void {
    this.companyId = this.$route.query.companyId as string
    this.oldData = cloneDeep(this.announcementForm)
  }

  initFormData(): void {
    this.singleSelection = null
    this.announcementForm = {
      title: '',
      context: '',
      publicTarget: [],
      publicFlag: false,
      publicStartDate: '',
      publicEndDate: '',
      publicPeriod: '',
      publicTargetType: ToolAnnouncementPublicTarget.LOGIN
    } as ToolAnnouncementFormData
    this.oldData = { ...this.announcementForm }
  }

  /**
   * テーブルデータを検索して、初期化を実行する。
   *
   * @returns {Promise<boolean>} 検索実行結果
   */
  async fetchTableData(): Promise<boolean> {
    try {
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > fetchTableData`)
      const data = await getAnnouncementListData()

      this.tableData = data.list as ToolAnnouncementData[]
      return true
    } catch (e) {
      this.tableData = []
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > fetchTableData: e=`, e)
      throw e
    }
  }

  /**
   * 公開対象初期化を実行する。
   */
  async fetchCompanyData(): Promise<void> {
    try {
      const param = {
        nameOrCode: ''
      }
      const data = (await getToolCompanyInfoData(param)).list as Array<ToolCompanyMasterData>
      data.map(item => {
        this.companyList.push({
          code: item.companyCode,
          name: item.companyName
        })
      })
    } catch (e) {
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > fetchCompanyData: e=`, e)
      throw e
    }
  }

  /**
   * パスワードルール
   */
  formRules: Form['rules'] = {
    // 件名
    title: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.announcementsInfo.title')]).toString(),
        trigger: 'blur'
      },
      // 50文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 50,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('gpsTracker.announcementsInfo.title'), 50]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 内容
    context: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('gpsTracker.announcementsInfo.context')]).toString(),
        trigger: 'blur'
      },
      // 120文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 120,
            message: i18n
              .t('vaildate.common.maxlength', [i18n.t('gpsTracker.announcementsInfo.context'), 120])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ]
  }

  /**
   * テーブルの選択したデータを変更する。
   *
   * @param {ToolAnnouncementData} row 選択されたデータ
   */
  handleCurrentChange(row: ToolAnnouncementData): void {
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO >  handleCurrentChange,`, row)
    this.formRef.clearValidate()
    if (!row) {
      this.singleSelection = row
    } else {
      this.singleSelection = row
      const {
        title,
        context,
        publicTarget,
        publicFlag,
        publicStartDate,
        publicEndDate,
        publicPeriod,
        publicTargetType
      } = row

      this.announcementForm = {
        title,
        context,
        publicTarget: publicTarget ? publicTarget.toString().split(',') : [],
        publicFlag: String(publicFlag) === ToolAnnouncementPublicFlag.PUBLIC ? true : false,
        publicStartDate,
        publicEndDate,
        publicPeriod,
        publicTargetType
      }
      this.oldData = cloneDeep(this.announcementForm)
    }
  }

  /**
   * 登録
   *
   */
  onAddClick(): void {
    this.$log.debug('TOOL_ANNOUNCEMENTS_INFO > onAddClick ')

    this.formRef.validate(valid => {
      if (!valid) return

      if (!this.correlationCheck()) return

      this.doAddAnnounceData()
    })
  }

  /**
   * チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  async dataValidate(): Promise<boolean> {
    try {
      const valid = await this.formRef.validate()
      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
          return
        }
      })

      return false
    }
  }

  /**
   * エラー項目をFOCUS
   *
   * @param {any} fields エラー項目
   * @returns {void}
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  focusOnValidField(fields: any): void {
    if (!fields) {
      return
    }

    const keys = Object.keys(fields)
    if (!keys || keys.length === 0) {
      return
    }

    const target = document.getElementById(`${keys[0]}`)
    target?.focus()
  }

  /**
   * 新規登録しないボタンをクリックしてデータを保存
   *
   * @returns {Promise<boolean>} 結果ステータス
   */
  async addAnnounceBefore(): Promise<boolean> {
    if (!(await this.dataValidate())) {
      return false
    }
    this.doAddAnnounceData()
    return true
  }

  /**
   * お知らせ新規登録。
   *
   */
  async doAddAnnounceData(): Promise<void> {
    const { title, context, publicTarget, publicFlag, publicStartDate, publicEndDate, publicTargetType } =
      this.announcementForm
    const param = {
      title: title,
      context: context,
      publicTarget: publicTarget.join(','),
      publicFlag: publicFlag ? ToolAnnouncementPublicFlag.PUBLIC : ToolAnnouncementPublicFlag.NOT_PUBLIC,
      publicStartDate: publicStartDate,
      publicEndDate: publicEndDate,
      publicTargetType: publicTargetType
    }
    try {
      await addAnnounce(param)

      this.saveDataAfter()
    } catch (e) {
      Vue.$log.debug('TOOL_ANNOUNCEMENTS_INFO > doAddAnnounceData: e=', e)
      throw e
    }
  }

  /**
   * 編集
   *
   */
  onEditClick(): void {
    this.$log.debug('TOOL_ANNOUNCEMENTS_INFO > onEditClick ')

    if (!helperService.isObjectDataChanged(Object(this.oldData), this.announcementForm)) {
      msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
      return
    }

    this.formRef.validate(valid => {
      if (!valid) return

      if (!this.correlationCheck()) return
      this.doUpdateAnnounceData()
    })
  }

  /**
   * お知らせ編集。
   *
   */
  async doUpdateAnnounceData(): Promise<void> {
    const { title, context, publicTarget, publicFlag, publicStartDate, publicEndDate, publicTargetType } =
      this.announcementForm
    const param = {
      id: this.singleSelection?.id,
      title,
      context,
      publicTarget: publicTarget.join(','),
      publicFlag: publicFlag ? ToolAnnouncementPublicFlag.PUBLIC : ToolAnnouncementPublicFlag.NOT_PUBLIC,
      publicStartDate,
      publicEndDate,
      publicTargetType
    } as ToolAnnouncementUpdateParam
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO >  doUpdateAnnounceData `, param)
    try {
      await updateAnnounce(param)
      this.saveDataAfter()
    } catch (e) {
      Vue.$log.debug('TOOL_ANNOUNCEMENTS_INFO > doUpdateAnnounceData: e=', e)
      throw e
    }
  }

  /**
   * 取消
   */
  onCancelClick(): void {
    Vue.$log.debug('TOOL_ANNOUNCEMENTS_INFO > onCancelButtonClick ')
    if (!helperService.isObjectDataChanged(Object(this.oldData), this.announcementForm)) {
      msgBoxService.info(i18n.t('messages.masterCommon.dataNotChanged').toString())
      return
    }

    const confirmCallback = () => {
      this.formRef.clearValidate()
      if (this.oldData) {
        this.announcementForm = { ...this.oldData }
      }
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * 戻るボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onBackButtonClick(): void {
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > onBackButtonClick`)

    const doBack = () => {
      this.doBack()
    }

    this.handMove(doBack)
  }

  /**
   * 前画面へ戻る
   *
   * @returns {void}
   */
  doBack(): void {
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > doBack`)
    this.$router.back()
  }

  /**
   * ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveData(): Promise<boolean> {
    this.addAnnounceBefore()
    return true
  }

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveDataAfter(): Promise<void> {
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > saveDataAfter`)
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString())
    this.fetchTableData()
    this.initFormData()
  }

  /**
   * 遷移する時の処理。
   *
   * @param {any} next 遷移処理
   * @param {boolean} needReset 保存失敗またはキャンセル時に、Routeをリセットするかどうか
   * @returns {void}
   */
  // eslint-disable-next-line
  handMove(next: any, needReset = false): void {
    this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > handMove`)
    if (!next) {
      return
    }

    if (!helperService.isObjectDataChanged(this.oldData, this.announcementForm)) {
      next()
      return
    }

    const confirmCallback = async () => {
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > handMove > confirmCallback`)
      try {
        this.saveDataAfter = async () => {
          msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), next)
        }
        if (!(await this.saveData()) && needReset) {
          this.$router.forward()
        }
      } catch (e) {
        this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > handMove`, e)
        if (needReset) {
          this.$router.forward()
        }
        throw e
      }
    }

    const rejectCallback = () => {
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > handMove > rejectCallback`)
      next()
    }

    const cancelCallback = () => {
      this.$log.debug(`TOOL_ANNOUNCEMENTS_INFO > handMove > cancelCallback`)
      if (!needReset) {
        return
      }

      this.$router.forward()
    }

    msgBoxService.confirmWithCancel(
      i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(),
      confirmCallback,
      rejectCallback,
      cancelCallback
    )
  }

  /**
   * ラジオボタンクリック時の処理
   */
  clearCompany(): void {
    this.announcementForm.publicTarget = []
  }

  /**
   * 相関チェックを行う
   *
   * @returns {boolean} 相関チェック結果
   */
  correlationCheck(): boolean {
    if (this.announcementForm.publicTargetType === ToolAnnouncementPublicTarget.COMPANY) {
      if (this.announcementForm.publicTarget.length === 0) {
        msgBoxService.info(
          i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.announcementsInfo.publicTarget')]).toString()
        )
        return false
      }
    }

    if (!this.announcementForm.publicStartDate) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.announcementsInfo.publicStartDate')]).toString()
      )
      return false
    }

    if (!this.announcementForm.publicEndDate) {
      msgBoxService.info(
        i18n.t('vaildate.common.selectRequired', [i18n.t('gpsTracker.announcementsInfo.publicEndDate')]).toString()
      )
      return false
    }

    if (isDateAfter(this.announcementForm.publicStartDate, this.announcementForm.publicEndDate, 'yyyy/MM/dd HH:mm')) {
      msgBoxService.info(
        i18n
          .t('vaildate.common.dateRange', [
            i18n.t('gpsTracker.announcementsInfo.publicStartDate'),
            i18n.t('gpsTracker.announcementsInfo.publicEndDate')
          ])
          .toString()
      )
      return false
    }

    return true
  }

  /**
   * 書式設定公開フラグ
   *
   * @param {ToolAnnouncementData} row 表の列
   * @returns {string} 表の内容
   */
  publicFlagFormat(row: ToolAnnouncementData): string {
    if (row.publicFlag) {
      if (row.publicFlag.toString() === ToolAnnouncementPublicFlag.PUBLIC) {
        return i18n.t('gpsTracker.announcementsInfo.public').toString()
      } else if (row.publicFlag.toString() === ToolAnnouncementPublicFlag.NOT_PUBLIC) {
        return i18n.t('gpsTracker.announcementsInfo.notPublic').toString()
      }
    }

    return ''
  }

  /**
   * 書式設定公開対象
   *
   * @param {ToolAnnouncementData} row 表の列
   * @returns {string} 表の内容
   */
  publicTargetFormat(row: ToolAnnouncementData): string {
    if (row.publicTargetType) {
      if (row.publicTargetType === ToolAnnouncementPublicTarget.LOGIN) {
        return i18n.t('gpsTracker.announcementsInfo.publicLogin').toString()
      } else if (row.publicTargetType === ToolAnnouncementPublicTarget.ALL) {
        return i18n.t('gpsTracker.announcementsInfo.publicAllCompany').toString()
      }
    }
    if (row.publicTarget) {
      const map: Record<string, string> = this.companyList.reduce((map, item) => {
        map[item.code] = item.name
        return map
      }, {} as Record<string, string>)

      const nameList = row.publicTarget
        .toString()
        .split(',')
        .map(code => map[code])
        .join(',')

      return nameList
    }

    return ''
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

.tti-main-header-shadow {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.announcement-form-style {
  width: 950px;
}

.tti-middle-area {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;

  .tti-bottom-left-area,
  .tti-bottom-right-area {
    display: flex;
    align-items: center;
    gap: 20px;
  }
}

.conn {
  font-size: large;
  margin-left: 10px;
  margin-right: 10px;
}

.radio-group {
  margin-top: 12px;
}

.radio-company {
  margin-top: 15px;
}

.select-company-item {
  margin-left: 100px;
  margin-top: -30px;
}

.select-company {
  height: 50px;
}

.custom-option {
  width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .el-form-item__label {
  font-size: 16px;
}

.tti-form-item-right {
  margin-left: 50px;
}

.el-textarea {
  margin-top: 6px;
}

.public-target {
  display: flex;

  .required-star-group {
    color: $C21;
    margin-top: 14px;
  }
}

.public-period {
  margin-top: 20px;
  display: flex;

  .required-star-date {
    color: $C21;
    margin-top: 14px;
  }
}
</style>
